'use client';

import React from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { LoginCard } from '@/ui/login-card';
import { CarPulseLogo } from '../car_pulse_logo';
import { DemoModal } from '@/ui/demo-modal';
import { useFormValidation, useFormSubmission } from '../../hooks';
import { emailValidator, requiredValidator } from '../auth-validators';
import { DASHBOARD_ROUTE } from '@/route';
import { loginAction } from '@/features/auth/action';
import styles from './login_form.module.css';

interface TitlePart {
  text: string;
  orange: boolean;
}

interface SlideContent {
  imageSrc: string;
  imageAlt: string;
  decorativeGradient: string;
  overlayGradient: string;
  titleParts: TitlePart[];
  subtitle: string;
}

const slides: SlideContent[] = [
  {
    imageSrc: '/1.png',
    imageAlt: 'Parc automobile – rangées de véhicules au lever du soleil',
    decorativeGradient: [
      'radial-gradient(ellipse 110% 55% at 15% 10%, rgba(255,142,43,0.08) 0%, transparent 65%)',
      'radial-gradient(ellipse 90% 70% at 85% 5%, rgba(28,90,140,0.14) 0%, transparent 55%)',
    ].join(', '),
    overlayGradient:
      'linear-gradient(to top, rgba(6,15,24,0.97) 0%, rgba(6,15,24,0.86) 22%, rgba(6,15,24,0.52) 50%, rgba(6,15,24,0.08) 75%, rgba(6,15,24,0.0) 100%)',
    titleParts: [
      { text: 'Avec ', orange: false },
      { text: 'CarPulse', orange: true },
      {
        text: ", prenez une longueur d'avance sur le marché automobile",
        orange: false,
      },
    ],
    subtitle:
      'Identifiez les offres à fort potentiel au bon moment, sans perdre de temps.',
  },
  {
    imageSrc: '/2.png',
    imageAlt: 'Intérieur habitacle Volvo – volant et cockpit',
    decorativeGradient: [
      'radial-gradient(ellipse 80% 50% at 60% 25%, rgba(255,142,43,0.05) 0%, transparent 70%)',
      'radial-gradient(ellipse 100% 40% at -10% 50%, rgba(255,255,255,0.03) 0%, transparent 60%)',
    ].join(', '),
    overlayGradient:
      'linear-gradient(to top, rgba(8,10,11,0.98) 0%, rgba(8,10,11,0.90) 20%, rgba(8,10,11,0.55) 48%, rgba(8,10,11,0.10) 72%, rgba(8,10,11,0.0) 100%)',
    titleParts: [
      { text: "Moins de bruit, plus d'", orange: false },
      { text: 'opportunités pertinentes', orange: true },
    ],
    subtitle: 'Des offres sélectionnées selon vos critères et vos objectifs.',
  },
  {
    imageSrc: '/3.png',
    imageAlt: 'Conseiller automobile en showroom avec tablette',
    decorativeGradient: [
      'radial-gradient(ellipse 120% 45% at 50% 0%, rgba(255,255,255,0.06) 0%, transparent 55%)',
      'radial-gradient(ellipse 70% 60% at 80% 15%, rgba(255,142,43,0.05) 0%, transparent 65%)',
    ].join(', '),
    overlayGradient:
      'linear-gradient(to top, rgba(14,14,14,0.96) 0%, rgba(14,14,14,0.84) 22%, rgba(14,14,14,0.48) 52%, rgba(14,14,14,0.06) 74%, rgba(14,14,14,0.0) 100%)',
    titleParts: [
      { text: 'Un espace complet pour ', orange: false },
      { text: 'piloter et optimiser', orange: true },
      { text: ' votre activité', orange: false },
    ],
    subtitle:
      'Suivez vos deals, gérez vos abonnements et mesurez vos performances en un seul endroit.',
  },
];

export const LoginForm: React.FC = () => {
  const router = useRouter();
  const [showPassword, setShowPassword] = React.useState(false);
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [isDemoModalOpen, setIsDemoModalOpen] = React.useState(false);

  const togglePassword = () => setShowPassword((v) => !v);

  const emailValidation = useFormValidation({
    initialValue: '',
    rules: [emailValidator],
    validateOnChange: false,
  });

  const passwordValidation = useFormValidation({
    initialValue: '',
    rules: [requiredValidator('Le mot de passe')],
    validateOnChange: false,
  });

  const { isSubmitting, error, handleSubmit } = useFormSubmission({
    onSubmit: async () => {
      const emailError = emailValidation.validate();
      const passwordError = passwordValidation.validate();

      if (emailError) {
        emailValidation.setError(emailError);
        throw new Error(emailError);
      }
      if (passwordError) {
        passwordValidation.setError(passwordError);
        throw new Error(passwordError);
      }

      const result = await loginAction(
        emailValidation.value,
        passwordValidation.value,
      );

      if (!result.success) {
        throw new Error(result.error ?? 'Erreur lors de la connexion');
      }

      router.replace(DASHBOARD_ROUTE);
      router.refresh();
      window.location.replace(DASHBOARD_ROUTE);
    },
  });

  React.useEffect(() => {
    const interval = setInterval(
      () => setCurrentSlide((prev) => (prev + 1) % slides.length),
      5000,
    );
    return () => clearInterval(interval);
  }, []);

  return (
    <div className={styles.root}>
      {/* ── Left panel – image carousel ───────────────────────── */}
      <div className={styles.panel} aria-hidden="true">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`${styles.slide} ${
              index === currentSlide ? styles.slideActive : styles.slideInactive
            }`}
          >
            {/* 1. Background photo — full bleed, object-fit: cover */}
            <Image
              src={slide.imageSrc}
              alt={slide.imageAlt}
              fill
              sizes="(min-width: 1536px) 560px, (min-width: 1280px) 48vw, 50vw"
              priority={index === 0}
              className={styles.slideImage}
            />

            {/* 2. Decorative ambient light layer over the photo */}
            <div
              className={styles.slideDecor}
              style={{ background: slide.decorativeGradient }}
            />

            {/* 3. Bottom vignette — darkens lower ~55% upwards */}
            <div
              className={styles.slideOverlay}
              style={{ background: slide.overlayGradient }}
            />

            {/* 4. Text content anchored to bottom */}
            <div className={styles.slideContent}>
              <h2 className={styles.slideTitle}>
                {slide.titleParts.map((part, i) =>
                  part.orange ? (
                    <span key={i} className={styles.orange}>
                      {part.text}
                    </span>
                  ) : (
                    <React.Fragment key={i}>{part.text}</React.Fragment>
                  ),
                )}
              </h2>

              <p className={styles.slideSubtitle}>{slide.subtitle}</p>

              <div className={styles.dots} role="tablist" aria-label="Slides">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    role="tab"
                    aria-selected={i === currentSlide}
                    aria-label={`Slide ${i + 1}`}
                    className={`${styles.dot} ${
                      i === currentSlide ? styles.dotActive : ''
                    }`}
                    onClick={() => setCurrentSlide(i)}
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Right panel – login form ───────────────────────────── */}
      <div className={styles.formSide}>
        <div className={styles.formInner}>
          <CarPulseLogo className={styles.logo} textColor="#FF8E2B" />
        </div>

        <LoginCard
          title="Connexion à votre espace"
          showForgotPassword={true}
          onSubmit={handleSubmit}
          emailValue={emailValidation.value}
          emailError={emailValidation.error}
          onEmailChange={emailValidation.handleChange}
          onEmailBlur={emailValidation.handleBlur}
          passwordValue={passwordValidation.value}
          passwordError={passwordValidation.error}
          onPasswordChange={passwordValidation.handleChange}
          onPasswordBlur={passwordValidation.handleBlur}
          showPassword={showPassword}
          onTogglePassword={togglePassword}
          isSubmitting={isSubmitting}
          error={error}
          buttonText="Se connecter"
        />
      </div>

      <DemoModal
        isOpen={isDemoModalOpen}
        onClose={() => setIsDemoModalOpen(false)}
        onScheduleDemo={() => {}}
      />
    </div>
  );
};

export default LoginForm;